import { useEffect, useState } from "react";
import { Phone, TrendingUp, DollarSign, Info, TriangleAlert } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const totalMonthlyLeads = 200;
const webFormLeadPercentage = 0.3;
const webFormLeads = totalMonthlyLeads * webFormLeadPercentage; // 60
const baseConversionRate = 0.15;
const improvedConversionRate = 0.25;
const tourToLeaseRate = 0.33;
const averageMonthlyRent = 1604;
const annualRentValuePerLease = averageMonthlyRent * 12;

const MissedOpportunityAnalysis = ({ secretShoppers, community }) => {
    const [metrics, setMetrics] = useState(null);

    useEffect(() => {
        if (!secretShoppers.length) return;

        let missed = 0;
        const now = new Date();

        secretShoppers.forEach((shopper) => {
            const createdAt = new Date(shopper.created_at);
            const twentyFourHours = 24 * 60 * 60 * 1000;
            if (now - createdAt < twentyFourHours) return;
            if (!shopper.has_incoming_call_24h) {
                missed += 1;
            }
        });

        const twentyFourHours = 24 * 60 * 60 * 1000;
        const total = secretShoppers.filter((shopper) => {
            const createdAt = new Date(shopper.created_at);
            return now - createdAt >= twentyFourHours;
        }).length;
        const missedRatio = total ? missed / total : 0;
        const missedFormLeads = Math.round(webFormLeads * missedRatio);
        const additionalTours = Math.round(missedFormLeads * (improvedConversionRate - baseConversionRate));
        const additionalLeases = Math.round(additionalTours * tourToLeaseRate);
        const missedMonthlyRevenue = additionalLeases * annualRentValuePerLease;
        const missedAnnualRevenue = missedMonthlyRevenue * 12;

        setMetrics({ missed, total, missedFormLeads, additionalLeases, missedMonthlyRevenue, missedAnnualRevenue });
    }, [secretShoppers]);

    const isSecretShopper = community?.status === 'secret-shopper';

    if (!metrics || metrics.missed === 0 || !isSecretShopper) return null;

    const tooltipText = `Calculated based on ${totalMonthlyLeads} leads per month, we assume ${webFormLeadPercentage * 100}% (about ${webFormLeads}) come through web forms. If ${metrics.missed} out of ${metrics.total} secret shops missed the 24-hour follow-up window, that’s roughly ${metrics.missedFormLeads} uncontacted leads. With a conversion boost from ${baseConversionRate * 100}% to ${improvedConversionRate * 100}% and a ${tourToLeaseRate * 100}% tour-to-lease rate, that’s ${metrics.additionalLeases} extra leases per month. At $${averageMonthlyRent.toLocaleString()} average rent, that’s $${metrics.missedMonthlyRevenue.toLocaleString()} in annual rent value per month — totaling over $${metrics.missedAnnualRevenue.toLocaleString()} in missed value per year.`;

    return (
        <TooltipProvider>
            <div className="mb-6">
                <div className="space-y-6">
                    <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl blur-lg"></div>
                        <div className="relative bg-white/90 backdrop-blur-sm border border-amber-200/50 rounded-xl p-4 shadow-sm">
                            <div className="flex items-center gap-3">
                                <TriangleAlert className="w-7 h-7 text-amber-300" />
                                <h1 className="text-xl font-bold text-slate-900">Missed Opportunity Analysis</h1>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="border border-slate-200 bg-white hover:shadow-md transition-shadow duration-200">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3 mb-2">
                                    <Phone className="w-4 h-4 text-red-500" />
                                    <span className="text-xs font-medium text-red-600 uppercase tracking-wide">
                                        No Call Within 24 Hours
                                    </span>
                                </div>
                                <div className="text-2xl font-bold text-slate-900 mb-1">{metrics.missed}/{metrics.total} Secret Shops</div>
                                <p className="text-xs text-slate-600">Missed the critical follow-up window</p>
                            </CardContent>
                        </Card>

                        <Card className="border border-slate-200 bg-white hover:shadow-md transition-shadow duration-200">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3 mb-2">
                                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                                    <span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Potential</span>
                                </div>
                                <div className="text-2xl font-bold text-slate-900 mb-1">+115% Conversions</div>
                                <p className="text-xs text-slate-600">
                                    Based on{" "}
                                    <a
                                        href="https://www.gong.io/resources/labs/should-you-leave-voicemails-when-cold-calling-heres-what-the-data-says/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-slate-600 underline hover:opacity-80"
                                    >
                                        Gong research
                                    </a>
                                    : following up with a call and voicemail increases conversions by 115% over email-only.
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="border border-slate-200 bg-white hover:shadow-md transition-shadow duration-200">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3 mb-2">
                                    <DollarSign className="w-4 h-4 text-blue-500" />
                                    <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Missed Potential Rent</span>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Info className="w-3 h-3 text-slate-400 hover:text-slate-600 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-xs">
                                            <p className="text-xs">
                                                {tooltipText}
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                                <div className="text-2xl font-bold text-slate-900 mb-1">${metrics.missedAnnualRevenue.toLocaleString()}/year</div>
                                <p className="text-xs text-slate-600">Estimated based on missed follow-ups</p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
}

export default MissedOpportunityAnalysis;